import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Tag from "@/components/base/Tag";
import { getGuideBySlug, guides } from "@/mocks/guides";
import { siteInfo } from "@/mocks/site";

export default function GuideDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const item = slug ? getGuideBySlug(slug) : undefined;

  if (!item) {
    return (
      <section className="w-full bg-background-50 py-20 md:py-28">
        <div className="mx-auto w-full max-w-[1240px] px-4 text-center md:px-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500" aria-hidden="true">
            <i className="ri-book-2-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该指南</h1>
          <p className="mt-3 text-sm text-foreground-600">这篇文章可能已经调整了地址。</p>
          <Link
            to="/guide"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true" />
            返回求证指南
          </Link>
        </div>
      </section>
    );
  }

  const others = guides.filter((guide) => guide.id !== item.id).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: item.excerpt,
    datePublished: item.date,
    dateModified: item.date,
    inLanguage: "zh-CN",
    articleSection: item.category,
    keywords: item.tags.join(","),
    url: `${siteInfo.url}/guide/${item.slug}`,
    author: { "@type": "Organization", name: siteInfo.name, url: siteInfo.url },
    publisher: { "@type": "Organization", name: siteInfo.name, url: siteInfo.url },
    image: item.cover,
  };

  return (
    <>
      <Seo
        title={`${item.title}｜求证指南 - 棱镜求证`}
        description={`${item.excerpt}（阅读约 ${item.readTime}，发布于 ${item.date}）`}
        keywords={`${item.tags.join(",")},求证指南,真相核验`}
        path={`/guide/${item.slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs
            items={[
              { label: "求证指南", path: "/guide" },
              { label: item.title },
            ]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Tag tone="accent" icon="ri-bookmark-line">
              {item.category}
            </Tag>
            <span className="text-xs text-foreground-500">阅读约 {item.readTime}</span>
            <span className="text-xs text-foreground-500">发布于 {item.date}</span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {item.excerpt}
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="w-full overflow-hidden rounded-lg border border-background-200">
              <div className="h-56 w-full bg-background-200 md:h-80">
                <img
                  src={item.cover}
                  alt={item.coverAlt}
                  title={`${item.title} - 求证指南配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <p className="mt-8 border-l-4 border-accent-400 bg-background-100 p-5 text-sm leading-relaxed text-foreground-800 md:text-base">
              {item.intro}
            </p>

            {item.sections.map((section) => (
              <section key={section.heading} className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {section.paragraphs.map((paragraph, index) => (
                    <p key={`${section.heading}-${index}`} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
                {section.points && section.points.length > 0 ? (
                  <ul className="mt-5 space-y-2.5 rounded-lg border border-background-200 bg-background-100 p-5">
                    {section.points.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950" aria-hidden="true">
                          <i className="ri-check-line text-xs" />
                        </span>
                        <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}

            <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">把这套方法用起来</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                在事件案例库中，你可以看到这些方法被应用在真实事件的核验过程里，包括时间线与官方回应的处理方式。
              </p>
              <Link
                to="/cases"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-stack-line text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">查看事件案例库</span>
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">本文目录</h2>
                <ol className="mt-3 space-y-2">
                  {item.sections.map((section, index) => (
                    <li key={section.heading} className="flex items-start gap-2 text-sm text-foreground-600">
                      <span className="font-label text-xs text-accent-700">{String(index + 1).padStart(2, "0")}</span>
                      <span className="leading-snug">{section.heading}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">继续阅读</h2>
                <ul className="mt-3 space-y-3">
                  {others.map((guide) => (
                    <li key={guide.id} className="border-t border-background-200 pt-3 first:border-0 first:pt-0">
                      <Link
                        to={`/guide/${guide.slug}`}
                        className="group block text-sm font-medium leading-snug text-foreground-800 transition-colors hover:text-accent-800"
                      >
                        {guide.title}
                      </Link>
                      <span className="mt-1 block text-xs text-foreground-500">{guide.readTime}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}